import { useState, useEffect, useRef } from "react";
import { Search, X, MapPin, Sparkles, Clock, Gift, Droplets, Zap, ChevronRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Smart symptom catalog — maps common client-language concerns to
// specific treatment landing pages so "dark circles" jumps straight to
// the Skin Boosters Eye Revival card, not a generic services page.
const SYMPTOM_CATALOG = [
  {
    en: "Dark circles",
    fr: "Cernes",
    terms: ["dark circles", "under eye", "under-eye", "eye bags", "hollow eyes", "tired eyes", "cernes", "yeux fatigués", "poches sous les yeux"],
    treatments: [
      { label: "Eye Revival — $449", route: "/skin-boosters?filter=core#eye-revival", why: "Under-eye Redensity 1 — softens dark circles & hollowness" },
      { label: "Eye Brightening Combo — $699", route: "/combos#combos-botox-booster", why: "Crow's Feet Botox + Under-Eye Glow" },
    ],
  },
  {
    en: "Fine lines & wrinkles",
    fr: "Rides et ridules",
    terms: ["wrinkles", "fine lines", "lines", "aging", "anti-aging", "rides", "ridules", "anti-âge"],
    treatments: [
      { label: "Full Upper Face Botox — $599", route: "/botox-ottawa#pricing", why: "7 areas · UNLIMITED units — smooths every expression line" },
      { label: "Baby Glow Infusion — $699", route: "/skin-boosters", why: "Full-face Redensity 1 — hydrates + smooths" },
    ],
  },
  {
    en: "Forehead lines",
    fr: "Rides du front",
    terms: ["forehead", "forehead lines", "11s", "11's", "frown lines", "glabellar", "front", "rides du front"],
    treatments: [
      { label: "Forehead + 11's Botox — $349", route: "/botox-ottawa#pricing", why: "The 2 most-requested areas · UNLIMITED units" },
    ],
  },
  {
    en: "Crow's feet",
    fr: "Pattes d'oie",
    terms: ["crows feet", "crow's feet", "smile lines eyes", "eye wrinkles", "pattes d'oie"],
    treatments: [
      { label: "Smooth Eyes Botox — $299", route: "/skin-boosters?filter=botox", why: "Botox for crow's feet · UNLIMITED units" },
    ],
  },
  {
    en: "Acne scars",
    fr: "Cicatrices d'acné",
    terms: ["acne scars", "acne", "scars", "pitted skin", "texture", "cicatrices", "acné"],
    treatments: [
      { label: "Microneedling 3-pack — $1,099", route: "/medical-microneedling#pricing", why: "Optimal collagen remodeling · dramatic scar improvement" },
      { label: "Vampire Facelift (PRP + Microneedling) — $750", route: "/prp-ottawa#treatment-areas", why: "PRP + microneedling — natural rebuild" },
    ],
  },
  {
    en: "Thin lips",
    fr: "Lèvres fines",
    terms: ["thin lips", "lips", "lip filler", "lip volume", "kissable lips", "lèvres", "lèvres fines"],
    treatments: [
      { label: "1 Syringe Filler + FREE Lip Flip — $499", route: "/lip-filler-ottawa#treatment-areas", why: "1ml filler + free Botox Lip Flip · UNLIMITED units" },
      { label: "½ Syringe Filler + FREE Lip Flip — $299", route: "/lip-filler-ottawa#treatment-areas", why: "Door-crasher intro price" },
    ],
  },
  {
    en: "Sagging jawline",
    fr: "Mâchoire relâchée",
    terms: ["sagging", "jawline", "jaw", "double chin", "loose skin", "jowls", "mâchoire", "cou"],
    treatments: [
      { label: "Nefertiti Jaw & Neck Lift — $479", route: "/combos#combos-botox", why: "Jawline + neck bands Botox · UNLIMITED units" },
      { label: "The Radiance Lift (Face + Neck) — $899", route: "/skin-boosters?filter=core", why: "Full face + neck Redensity 1 · save $299" },
    ],
  },
  {
    en: "Crepey neck",
    fr: "Cou crêpé",
    terms: ["crepey", "neck", "necklace lines", "neck lines", "turkey neck", "cou", "lignes du cou"],
    treatments: [
      { label: "Neck Refinement — $499", route: "/skin-boosters?filter=core", why: "Full neck Redensity 1 — smooths necklace lines" },
      { label: "Neck Perfection Combo — $799", route: "/combos#combos-botox-booster", why: "Neck Bands Botox + Neck Glow" },
    ],
  },
  {
    en: "Dull skin",
    fr: "Peau terne",
    terms: ["dull skin", "dull", "no glow", "tired skin", "radiance", "brightness", "peau terne", "éclat"],
    treatments: [
      { label: "Baby Glow Infusion — $699", route: "/skin-boosters", why: "Full-face Redensity 1 · #1 recommended for glow" },
      { label: "HydraFacial + Laser Genesis — $349", route: "/combos#combos-laser", why: "Complete facial makeover · save $21" },
    ],
  },
  {
    en: "Sun damage",
    fr: "Dommages solaires",
    terms: ["sun damage", "sun spots", "pigmentation", "melasma", "dark spots", "brown spots", "taches"],
    treatments: [
      { label: "Décolleté Glow — $599", route: "/skin-boosters?filter=core", why: "Chest Redensity 1 — repairs sun damage" },
      { label: "Laser Genesis (in HF combo) — $349", route: "/combos#combos-laser", why: "Redness, texture & pigmentation refinement" },
    ],
  },
  {
    en: "Unwanted hair",
    fr: "Poils indésirables",
    terms: ["hair removal", "unwanted hair", "laser hair", "shaving", "brazilian", "épilation"],
    treatments: [
      { label: "Laser Hair Removal · All Areas + Promos", route: "/laser-hair-removal", why: "Single-area pricing, The House Package (Buy 6 → 6 small FREE), and Head-to-Toe Unlimited" },
      { label: "Men's Laser Hair Removal · By Area", route: "/laser-hair-removal", why: "Back, shoulders, neck, chest — same priced-by-area menu as everyone" },
    ],
  },
  {
    en: "Aging hands",
    fr: "Mains vieillies",
    terms: ["hands", "veiny hands", "wrinkled hands", "crepey hands", "aging hands", "mains"],
    treatments: [
      { label: "Baby Glow Hands — $399", route: "/skin-boosters?filter=core", why: "1 syringe Redensity 1 · restores volume + softens veins" },
      { label: "Baby Glow Max Hands — $599", route: "/skin-boosters?filter=core", why: "Expanded coverage + between-fingers treatment" },
    ],
  },
  {
    en: "Hair loss / thinning",
    fr: "Perte de cheveux",
    terms: ["hair loss", "thinning", "balding", "hair thinning", "scalp", "perte de cheveux"],
    treatments: [
      { label: "PRP Hair Loss (4 sessions) — $2,499", route: "/prp-ottawa#treatment-areas", why: "Complete hair restoration program · stimulates dormant follicles" },
    ],
  },
  {
    en: "Volume loss / gaunt cheeks",
    fr: "Perte de volume",
    terms: ["volume loss", "gaunt", "sunken cheeks", "hollow cheeks", "cheeks", "cheekbones", "joues", "volume"],
    treatments: [
      { label: "Sculptra + Full Upper Face Botox — $1,299", route: "/combos#combos-laser", why: "Long-term collagen rebuild + expression smoothing" },
      { label: "2ml Filler + FREE Botox — $950", route: "/dermal-fillers#pricing", why: "Cheek/jaw contour + free Botox area" },
    ],
  },
  {
    en: "Smoker's lines",
    fr: "Lignes de fumeur",
    terms: ["smokers lines", "smoker's lines", "lip lines", "perioral", "vertical lip lines", "lignes de fumeur"],
    treatments: [
      { label: "Smokers Lines Combo — $899", route: "/combos#combos-botox-filler", why: "2 syringes filler + 16 units Botox around the lip line" },
    ],
  },
  // Feb 2026 · Red-light / LED concern mapping — route clients to the
  // facials that include it rather than hiding behind a generic ingredient.
  {
    en: "Red light / LED therapy",
    fr: "Luminothérapie / LED",
    terms: ["red light", "red light therapy", "led", "led therapy", "led light", "light therapy", "photobiomodulation", "celluma", "infrared", "pbm", "cold laser", "low level light therapy", "lllt", "luminothérapie", "lumière rouge"],
    treatments: [
      { label: "Crown Deluxe Hydrafacial — $199", route: "/hydrafacial", why: "LED Therapy + Hydrafacial + included booster · easiest way to get red-light results" },
      { label: "Skin Tightening 360", route: "/laser-and-skin", why: "4-step: Diamond Tip / Dermaplaning + Laser Genesis + Hydrating Mask + LED Therapy" },
      { label: "Microneedling finish with LED", route: "/medical-microneedling", why: "LED soothes + accelerates healing after medical microneedling" },
    ],
  },
  // Feb 2026 · CO₂Lift carboxytherapy concern mapping
  {
    en: "No-needle lift / Oxygen glow",
    fr: "Lift sans aiguille / Oxygénation",
    terms: ["no needle", "no needles", "needle free", "needle-free", "carboxy", "carboxytherapy", "oxygen facial", "oxygen glow", "co2", "co 2", "co2 lift", "co2lift", "bohr effect", "topical co2", "oxygenate"],
    treatments: [
      { label: "CO₂Lift Carboxytherapy — explained", route: "/co2lift-ottawa", why: "The no-needle lift · topical CO₂ · 45 minutes to a visible glow" },
      { label: "CO₂Lift Skin Mousse — $375", route: "/shop/CO2LIFT-MOUSSE-60G", why: "Daily carboxy moisturizer · retinol + Vit C + AHA/BHA + HA in one step" },
      { label: "CO₂Lift GLO Double Set — $275", route: "/shop/CO2LIFT-GLO-DOUBLE", why: "2 at-home carboxy masks · 45 min to a visibly lifted glow" },
    ],
  },
  // Feb 2026 · Latisse lash growth concern mapping
  {
    en: "Longer / thicker lashes",
    fr: "Cils plus longs et épais",
    terms: ["longer lashes", "thicker lashes", "lash growth", "sparse lashes", "short lashes", "lash serum", "latisse", "eyelash growth", "lash enhancement", "bimatoprost", "lash rx", "lash prescription", "cils longs", "cils épais"],
    treatments: [
      { label: "Latisse® explained — Rx lash growth", route: "/latisse-ottawa", why: "Prescription bimatoprost 0.03% · longer, thicker, darker lashes in 12-16 weeks" },
      { label: "Latisse® 5 mL — $240 (best value)", route: "/shop/LATISSE-5ML", why: "10-14 week supply · the full transformation in one bottle · Rx + consult included" },
      { label: "Latisse® 3 mL — $149 (starter)", route: "/shop/LATISSE-3ML", why: "Starter 6-8 week supply · perfect first bottle" },
    ],
  },
  // Feb 2026 · Intimate wellness concern mapping (CO₂Lift V)
  {
    en: "Intimate rejuvenation",
    fr: "Rajeunissement intime",
    terms: ["intimate", "vaginal", "feminine", "post partum", "postpartum", "post-partum", "menopause", "vaginal dryness", "laxity", "intimate dryness", "vaginal rejuvenation", "feminine rejuvenation"],
    treatments: [
      { label: "CO₂LIFT® V 5-Count Kit — $349", route: "/shop/CO2LIFT-V-5PACK", why: "Doctor-formulated intimate carboxy gel · 15-day course · hormone-free" },
      { label: "CO₂LIFT® V 3-Count Starter — $452", route: "/shop/CO2LIFT-V-3PACK", why: "3-treatment intro to CO₂LIFT® V" },
    ],
  },
];

const SearchModal = ({ isOpen, onClose, scrollToSection }) => {
  const { t, language } = useLanguage();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [symptomResults, setSymptomResults] = useState([]);
  const [catalogServices, setCatalogServices] = useState([]);
  const [storeProducts, setStoreProducts] = useState([]);
  const inputRef = useRef(null);

  // Fetch all services from Square catalog once
  // Oct 20 2026 — index the LIVE Square menu of all 3 clinics (with
  // treatment numbers) instead of the static /api/services list, so the
  // search knows every current promo, hides retired/duplicate items and
  // finds "#12" / "A7" by number.
  useEffect(() => {
    if (catalogServices.length > 0) return;
    if (!window.__klSearchCatalogPromise) {
      window.__klSearchCatalogPromise = Promise.all(["3", "1", "2"].map((id) =>
        axios.get(`${API}/api/square/catalog-services/${id}`, { timeout: 45000 })
          .then((r) => (r.data?.services || []).map((svc) => ({ ...svc, _loc: id })))
          .catch(() => [])
      ));
    }
    window.__klSearchCatalogPromise.then((lists) => {
      const seen = new Map();
      lists.flat().forEach((svc) => {
        const key = (svc.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
        if (!key) return;
        const prev = seen.get(key);
        if (!prev) seen.set(key, { ...svc, locations: [svc._loc] });
        else { prev.locations.push(svc._loc); if (!prev.code && svc.code) prev.code = svc.code; }
      });
      const merged = [...seen.values()];
      if (merged.length) setCatalogServices(merged);
      else axios.get(`${API}/api/services`).then((res) => setCatalogServices(Array.isArray(res.data) ? res.data : (res.data.services || []))).catch(() => {});
    });
  }, [catalogServices.length]);

  // Feb 2026 · Karine ask — auto-ingest the full shop catalog so every
  // skincare SKU (CO₂Lift, SkinCeuticals, Éminence, Colorescience, Lumisque,
  // Latisse, future brands…) is searchable the moment it's seeded, with no
  // code change needed. Pulled once per mount; results merge into the
  // existing searchData pipeline.
  useEffect(() => {
    if (storeProducts.length > 0) return;
    axios.get(`${API}/api/store/products?limit=500`)
      .then(res => {
        const items = Array.isArray(res.data)
          ? res.data
          : (res.data.products || res.data.items || []);
        setStoreProducts(items);
      })
      .catch(() => {});
  }, [storeProducts.length]);

  // Static search items (pages, locations, etc.)
  const staticItems = [
    { name: "Ottawa Location", category: "Location", description: "1390 Prince of Wales Drive, Main Floor", section: "locations", icon: MapPin, keywords: ["ottawa", "prince of wales", "clinic"] },
    { name: "Carleton Place Location", category: "Location", description: "163 Bridge Street, Downtown, Carleton Place, ON", section: "locations", icon: MapPin, keywords: ["carleton", "bridge", "clinic"] },
    { name: "Kanata Location", category: "Location", description: "320 March Road, Sixth Floor, Clinic 603, Kanata, ON", section: "locations", icon: MapPin, keywords: ["kanata", "march", "clinic"] },
    { name: "About Karine Lauzon RN", category: "Page", description: "35+ years of clinical expertise", section: "about", icon: Clock, keywords: ["about", "karine", "nurse", "experience", "bio"] },
    { name: "Contact Us", category: "Page", description: "Get in touch with our team", section: "contact", icon: Clock, keywords: ["contact", "email", "phone", "message"] },
    { name: "Gift Cards", category: "Page", description: "Purchase gift certificates", section: "gift-certificates", icon: Gift, keywords: ["gift", "certificate", "present", "voucher"] },
    { name: "Aftercare Instructions", category: "Page", description: "Post-treatment care guide", section: "aftercare", icon: Clock, keywords: ["aftercare", "post", "recovery", "care"] },
    { name: "Testimonials & Reviews", category: "Page", description: "See what our clients say", section: "testimonials", icon: Clock, keywords: ["reviews", "testimonials", "feedback"] },
    { name: "Current Promotions", category: "Page", description: "Special deals and offers", section: "promotions", icon: Sparkles, keywords: ["promotions", "deals", "offers", "specials", "discount", "promo"] },
    { name: "Crown Reserve Rewards", category: "Page", description: "Loyalty points program", section: "rewards", icon: Gift, keywords: ["rewards", "loyalty", "points", "crown", "redeem"] },
    { name: "Karine's Aesthetic Guide", category: "Page", description: "Skincare tips, quizzes & blog", section: "aesthetic-guide", icon: Sparkles, keywords: ["aesthetic", "guide", "skincare", "quiz", "blog", "tips", "karine"] },
    { name: "Financing Options", category: "Page", description: "Flexible payment plans available", section: "finance", icon: Clock, keywords: ["finance", "financing", "payment", "plan", "paybright"] },
    { name: "The Renergie Package", category: "Signature Treatment", description: "$1,399 +tax — 2 ML RHA1 Filler + 3 ML Redensity 1 Skin Booster", section: "/dermal-fillers", icon: Sparkles, keywords: ["renergie", "package", "rha1", "redensity", "filler", "booster", "combo", "aging", "fine lines", "crepey", "hydration", "glow", "signature", "1399"] },
    { name: "Dermal Fillers", category: "Treatment Page", description: "Natural volume restoration with premium HA fillers — Teoxane & Revanesse", section: "/dermal-fillers", icon: Sparkles, keywords: ["dermal", "filler", "fillers", "volume", "lips", "cheeks", "jawline", "teoxane", "revanesse", "rha", "hyaluronic"] },
    { name: "Skin Boosters & Combos", category: "Treatment Page", description: "Redensity 1 treatments — 30+ packages for face, neck, hands & more", section: "/skin-boosters", icon: Droplets, keywords: ["skin", "booster", "boosters", "redensity", "baby glow", "hydration", "glow", "combo", "botox combo", "neck", "decollete", "hands"] },
    { name: "Botox Treatments", category: "Treatment Page", description: "Unlimited units by area — flat rate pricing", section: "/skin-boosters?filter=botox", icon: Sparkles, keywords: ["botox", "wrinkle", "forehead", "crow", "11s", "frown", "brow", "lip flip", "nefertiti", "neck bands", "unlimited"] },
    { name: "Sculptra", category: "Treatment Page", description: "Collagen bio-stimulator — results last 2+ years", section: "/sculptra", icon: Sparkles, keywords: ["sculptra", "collagen", "bio-stimulator", "stimulator", "plla", "volume", "aging", "natural"] },
    { name: "Microneedling & PRP", category: "Treatment Page", description: "Skin rejuvenation with micro-needling and platelet-rich plasma", section: "/skin-boosters", icon: Sparkles, keywords: ["microneedling", "prp", "vampire", "facelift", "platelet", "needling", "collagen"] },
    { name: "Virtual Consultation", category: "Service", description: "Free video consultation with Karine from home", section: "/virtual-consultation-guide", icon: Clock, keywords: ["virtual", "video", "consultation", "zoom", "remote", "online", "free"] },
    { name: "Medical Microneedling", category: "Treatment Page", description: "PRP, Exosomes, VAMP (Salmon Sperm) — from $350", section: "/medical-microneedling", icon: Sparkles, keywords: ["microneedling", "micro", "needling", "prp", "vampire", "facial", "exosomes", "vamp", "salmon", "pdrn", "scar", "collagen", "texture", "pores"] },
    { name: "Before & After Gallery", category: "Page", description: "Real client transformation photos", section: "/before-after", icon: Sparkles, keywords: ["before", "after", "gallery", "results", "photos", "transformation"] },
    // ─── Oct 20 2026 · new booking experience ───
    { name: "Full Treatment Menu — book in 2 taps", category: "Page", description: "Every treatment with its number, price and length · search, filter by category, book a time", section: "/book/menu", icon: Sparkles, keywords: ["menu", "full menu", "book", "booking", "treatments", "all treatments", "price list", "prices", "number", "treatment number"] },
    { name: "Book by treatment number", category: "How to book", description: "Saw a number on a flyer or receipt? Type it here (e.g. 12 or A7) or say it to Lumi", section: "/book/menu", icon: Sparkles, keywords: ["number", "code", "treatment number", "#", "rebook", "flyer", "receipt"] },
    { name: "Winter Beauty Packages", category: "Promotion", price: "$1,449", description: "Non-Surgical Facelift Plus with Redensity 1 Skin Booster — Full Face & Neck Botox + 3 mL skin booster · 60 min", section: "/t/1", icon: Sparkles, keywords: ["winter", "package", "packages", "facelift", "non surgical", "non-surgical", "nefertiti", "redensity", "skin booster", "1449", "promo"] },
    { name: "Laser Hair Removal — pick your area", category: "Treatment Page", description: "Choose the body area first, then a single session or the Head-to-Toe Unlimited plan ($249/mo) · Ottawa", section: "/book?location=3", icon: Sparkles, keywords: ["laser", "hair removal", "laser hair", "unlimited", "head to toe", "bikini", "brazilian", "underarms", "legs", "arms", "face", "area"] },
    { name: "My Account — rebook, reschedule or cancel", category: "Page", description: "Sign in by text or email · see your appointments, rebook in one tap, reschedule, cancel, earlier openings", section: "/client-portal", icon: Clock, keywords: ["account", "my account", "login", "sign in", "appointments", "reschedule", "cancel", "rebook", "portal", "earlier", "openings", "last minute"] },
    { name: "Last Minute Club — earlier openings", category: "Page", description: "Grab a cancelled spot today or this week · text alerts when an earlier slot with your provider opens", section: "/client-portal", icon: Clock, keywords: ["last minute", "club", "cancellation", "earlier", "opening", "openings", "today", "text me", "alert"] },

    // ─── ALL COMBOS (aggregator page + individual signature combos) ────
    { name: "All Combo Packages", category: "Treatment Page", description: "Every combo we offer — Botox, Filler, Booster, Laser + VIP — bullet pricing", section: "/combos", icon: Sparkles, keywords: ["combos", "combo", "packages", "bundle", "makeover", "vip", "nefertiti", "glow", "big 3", "makeover 8", "the works"] },
    { name: "Makeover 9 — Redensity 1 Full Face + Neck", category: "Signature Combo", price: "$899", description: "Redensity 1 Skin Booster · Full Face + Neck · Afterpay 4 × $224.75", section: "/combos#combos-botox", icon: Sparkles, keywords: ["makeover 9", "redensity", "full face", "neck", "skin booster", "flagship"] },
    { name: "Nefertiti Jaw & Neck Lift", category: "Signature Combo", price: "$479", description: "Jawline + Neck Bands Botox · UNLIMITED units · lasts 3-4 months", section: "/combos#combos-botox", icon: Sparkles, keywords: ["nefertiti", "jaw", "jawline", "neck", "lift", "platysma", "sagging", "sculpt"] },
    { name: "The Nefertiti Glow", category: "Signature Combo", price: "$899", description: "Nefertiti Lift Botox + 3ml Redensity 1 · elegant contour + radiant glow", section: "/combos#combos-botox-booster", icon: Sparkles, keywords: ["nefertiti", "glow", "combo", "botox", "booster", "jawline", "neck"] },
    { name: "The #1 Choice Glow", category: "Signature Combo", price: "$1,199", description: "Full Face Botox + 3ml Redensity 1 · our top-picked upper-face transformation", section: "/combos#combos-botox-booster", icon: Sparkles, keywords: ["number 1", "no 1", "choice", "glow", "full face", "botox", "booster"] },
    { name: "The Big 3 Glow", category: "Signature Combo", price: "$1,099", description: "Big 3 Botox (Crow's Feet + 11's + Forehead) + Baby Glow", section: "/combos#combos-botox-booster", icon: Sparkles, keywords: ["big 3", "glow", "crows feet", "11s", "forehead", "baby glow"] },
    { name: "Ultimate Transformation", category: "Filler + Booster Combo", price: "$2,799", description: "3 filler syringes + 3 Redensity 1 syringes · Save $600", section: "/combos#combos-filler-booster", icon: Sparkles, keywords: ["ultimate", "transformation", "flagship", "filler", "booster", "save"] },

    // ─── SKIN BOOSTERS (top individual treatments) ─────────────────
    { name: "Baby Glow Infusion — Full Face", category: "Skin Booster", price: "$699", description: "Full face Redensity 1 · #1 recommended booster · natural glow", section: "/skin-boosters?filter=core", icon: Droplets, keywords: ["baby glow", "full face", "redensity", "glow", "hydration", "top pick"] },
    { name: "The Radiance Lift (Face + Neck)", category: "Skin Booster", price: "$899", description: "Full face + full neck Redensity 1 · save $299", section: "/skin-boosters?filter=core", icon: Droplets, keywords: ["radiance", "lift", "face", "neck", "necklace lines", "crepey"] },
    { name: "Eye Revival", category: "Skin Booster", price: "$449", description: "Under-eye Redensity 1 · softens dark circles + fine lines", section: "/skin-boosters?filter=core", icon: Droplets, keywords: ["eye", "revival", "under eye", "dark circles", "eye bags", "hollow"] },
    { name: "Neck Refinement", category: "Skin Booster", price: "$499", description: "Full neck Redensity 1 · smooths necklace lines & crepey texture", section: "/skin-boosters?filter=core", icon: Droplets, keywords: ["neck", "refinement", "necklace lines", "crepey", "vertical bands"] },
    { name: "Décolleté Glow", category: "Skin Booster", price: "$599", description: "Chest Redensity 1 · repairs sun damage + smooths chest wrinkles", section: "/skin-boosters?filter=core", icon: Droplets, keywords: ["decollete", "chest", "sun damage", "cleavage"] },
    { name: "Baby Glow Hands", category: "Skin Booster", price: "$399", description: "Both hands Redensity 1 · restores volume + softens veins", section: "/skin-boosters?filter=core", icon: Droplets, keywords: ["hands", "aging hands", "veiny hands", "crepey"] },
    { name: "Microneedling + 3ML Skin Booster", category: "Combo Best Value", price: "$899", description: "Medical-grade microneedling + full 3ml Redensity 1 infusion", section: "/skin-boosters?filter=combos", icon: Sparkles, keywords: ["microneedling", "skin booster", "combo", "best value", "collagen"] },

    // ─── BOTOX INDIVIDUAL PACKAGES ─────────────────────────────────
    { name: "Full Upper Face Botox Package", category: "Botox Package", price: "$599", description: "7 areas · UNLIMITED units per area · lasts 3-4 months", section: "/botox-ottawa#pricing", icon: Sparkles, keywords: ["full upper face", "botox", "7 areas", "forehead", "11s", "unlimited"] },
    { name: "Botox Big 3", category: "Botox Package", price: "$499", description: "Crow's Feet + 11's + Forehead · UNLIMITED units", section: "/botox-ottawa#pricing", icon: Sparkles, keywords: ["big 3", "crows feet", "11s", "forehead", "botox popular"] },
    { name: "Forehead + 11's Botox", category: "Botox Package", price: "$349", description: "The 2 most-requested areas · UNLIMITED units", section: "/botox-ottawa#pricing", icon: Sparkles, keywords: ["forehead", "11s", "frown", "botox"] },
    { name: "Smooth Eyes (Crow's Feet)", category: "Botox Area", price: "$299", description: "Botox for crow's feet · UNLIMITED units · lasts 3-4 months", section: "/skin-boosters?filter=botox", icon: Sparkles, keywords: ["smooth eyes", "crows feet", "eye wrinkles", "smile lines"] },
    { name: "Botox Ottawa Landing Page", category: "Treatment Page", description: "Every Botox package + areas — flat-rate UNLIMITED units", section: "/botox-ottawa", icon: Sparkles, keywords: ["botox", "ottawa", "neuromodulator", "wrinkles"] },

    // ─── FILLER PACKAGES (individual + Ottawa pages) ───────────────
    { name: "1 Syringe Filler + FREE Lip Flip", category: "Filler Package", price: "$499", description: "1ml filler + free Botox area · UNLIMITED units on free area", section: "/dermal-fillers#pricing", icon: Sparkles, keywords: ["1 syringe", "filler", "lip flip", "free botox", "promo"] },
    { name: "2 Syringes Filler + FREE Botox", category: "Filler Package", price: "$950", description: "2ml filler + free Jelly Roll, Smile Lift OR Lip Flip Botox", section: "/dermal-fillers#pricing", icon: Sparkles, keywords: ["2 syringes", "filler", "free botox", "jelly roll", "smile lift"] },
    { name: "3 Syringes Filler + FREE Botox", category: "Filler Package", price: "$1,349", description: "3ml filler + free 11's OR Forehead Botox · UNLIMITED units", section: "/dermal-fillers#pricing", icon: Sparkles, keywords: ["3 syringes", "filler", "free botox", "best value"] },
    { name: "Lip Filler Ottawa", category: "Treatment Page", description: "Lip filler packages with free Botox add-ons", section: "/lip-filler-ottawa", icon: Sparkles, keywords: ["lip", "filler", "ottawa", "kissable", "volume"] },
    { name: "Dermal Filler Ottawa", category: "Treatment Page", description: "Dermal filler packages across all areas — Ottawa clinic", section: "/dermal-filler-ottawa", icon: Sparkles, keywords: ["dermal", "filler", "ottawa"] },

    // ─── VIP PROGRAMS ─────────────────────────────────────────────
    { name: "Crown Reserve Signature (Annual VIP)", category: "VIP Program", price: "$4,999", description: "Full year: 4 Botox + 3 Face/Neck/Décolleté Boosters + maintenance · VIP perks", section: "/combos#combos-vip", icon: Sparkles, keywords: ["crown reserve", "vip", "annual", "membership", "signature"] },
    { name: "Ultimate Skin Transformation (3-Month)", category: "VIP Program", price: "$3,499", description: "3 Full Upper Face Botox + 3 Face+Neck Redensity · progress photos", section: "/combos#combos-vip", icon: Sparkles, keywords: ["ultimate", "transformation", "3 month", "vip", "premium"] },
    { name: "VIP Glass Skin Experience", category: "VIP Program", price: "$1,999", description: "2 Full Upper Face Botox + 2 Full Face Redensity + aftercare kit", section: "/combos#combos-vip", icon: Sparkles, keywords: ["vip", "glass skin", "luxury", "aftercare kit"] },
    { name: "Signature Skin Membership", category: "Membership", price: "$249/month", description: "1 booster every 60-90 days + 10% off add-ons + priority booking", section: "/skin-boosters?filter=programs", icon: Sparkles, keywords: ["membership", "monthly", "subscription", "quarterly"] },

    // ─── LASER + MICRONEEDLING + PRP ─────────────────────────────
    { name: "Laser & Skin — Full Menu", category: "Treatment Page", description: "Laser hair removal packages + Laser Genesis + HydraFacial + facials", section: "/laser-and-skin", icon: Zap, keywords: ["laser", "hair removal", "genesis", "hydrafacial", "facial", "skin"] },
    { name: "RF Microneedling + Full Upper Face Botox", category: "Premium Combo", price: "$1,099", description: "75 min · Karine's premium anti-ageing combo", section: "/combos#combos-laser", icon: Sparkles, keywords: ["rf", "microneedling", "botox", "premium", "anti-aging"] },
    { name: "HydraFacial + Laser Genesis Combo", category: "Facial Combo", price: "$349", description: "Complete facial makeover · save $21", section: "/combos#combos-laser", icon: Sparkles, keywords: ["hydrafacial", "laser genesis", "facial", "makeover"] },
    { name: "PRP Single Treatment", category: "PRP", price: "$699", description: "Face, neck OR scalp rejuvenation · your own blood", section: "/prp-ottawa#treatment-areas", icon: Sparkles, keywords: ["prp", "platelet", "single", "vampire"] },
    { name: "PRP 3 Treatment Package", category: "PRP", price: "$1,899", description: "Best value for optimal collagen remodeling", section: "/prp-ottawa#treatment-areas", icon: Sparkles, keywords: ["prp", "3 treatment", "package", "best value"] },
    { name: "PRP Hair Loss (4 Treatments)", category: "PRP Hair", price: "$2,499", description: "Complete hair restoration · stimulates dormant follicles", section: "/prp-ottawa#treatment-areas", icon: Sparkles, keywords: ["prp", "hair loss", "thinning", "scalp", "balding", "4 sessions"] },
    { name: "Vampire Facelift (PRP + Microneedling)", category: "Combo", price: "$750", description: "PRP + microneedling · ultimate natural rejuvenation", section: "/prp-ottawa#treatment-areas", icon: Sparkles, keywords: ["vampire", "facelift", "prp", "microneedling"] },
    { name: "Microneedling 1 / 2 / 3 Treatments", category: "Microneedling Package", description: "$350 / $649 / $1,099 · progressive collagen remodeling", section: "/medical-microneedling#pricing", icon: Sparkles, keywords: ["microneedling", "single", "3 treatments", "package", "pricing"] },
    { name: "Microneedling Ottawa", category: "Treatment Page", description: "Medical microneedling at the Ottawa clinic", section: "/microneedling-ottawa", icon: Sparkles, keywords: ["microneedling", "ottawa", "medical"] },
    { name: "Sculptra Ottawa", category: "Treatment Page", description: "Sculptra bio-stimulator packages · 2+ year collagen rebuild", section: "/sculptra-ottawa", icon: Sparkles, keywords: ["sculptra", "ottawa", "biostimulator", "collagen"] },

    // ─── ACCOUNT / CART / BOOKING SHORTCUTS ──────────────────────
    { name: "My Client Portal", category: "Account", description: "Bookings, loyalty points, membership & invoices", section: "/client-portal", icon: Clock, keywords: ["portal", "account", "my account", "sign in", "points", "loyalty", "history"] },
    { name: "Book an Appointment", category: "Booking", description: "See real-time slots at your preferred clinic", section: "/book", icon: Clock, keywords: ["book", "booking", "appointment", "schedule", "slot"] },
    { name: "Join the Waitlist", category: "Booking", description: "Get notified when a slot opens sooner", section: "/last-minute-club", icon: Clock, keywords: ["waitlist", "cancellation", "sooner", "priority"] },
    { name: "Cart & Checkout", category: "Cart", description: "Review treatments in your cart and check out", section: "/book", icon: Sparkles, keywords: ["cart", "checkout", "basket", "review"] },

    // ─── LOYALTY / MEMBERSHIP ───────────────────────────────────
    { name: "Early Booking Bonus — 75 pts", category: "Loyalty", description: "Book 30+ days ahead and earn 75 Crown points automatically", section: "rewards", icon: Gift, keywords: ["early booking", "bonus", "75 points", "crown", "loyalty"] },
    { name: "Quick Rebook Bonus — 100 pts", category: "Loyalty", description: "Rebook within 60 days of your last appointment and earn 100 points", section: "rewards", icon: Gift, keywords: ["quick rebook", "rebook", "100 points", "loyalty", "returning"] },
    { name: "Refer a Friend — $50 credit", category: "Loyalty", description: "Give $50, get $50 · unlimited referrals", section: "/refer", icon: Gift, keywords: ["refer", "friend", "50", "credit", "referral"] },

    // ─── CROWN GAMES LOUNGE ─────────────────────────────────────
    { name: "Crown Games Lounge", category: "Entertainment", description: "Play Baby Glow bubbles, Zen Garden, Water Ripple, Block Blast & more while you wait", section: "/lounge", icon: Sparkles, keywords: ["lounge", "games", "crown", "bubble", "zen", "water ripple", "block blast", "entertainment"] },
    { name: "Bubble Encyclopedia", category: "Collectible Gallery", description: "Discover 100+ bubble, balloon & wildlife collectibles from the Lounge games", section: "/lounge/encyclopedia", icon: Sparkles, keywords: ["encyclopedia", "collectible", "gallery", "bubble", "balloon"] },

    // ─── RED LIGHT / LED THERAPY (Feb 2026 · Karine ask) ─────────
    // We don't sell LED/red-light therapy as a standalone service, but
    // it's included in every Hydrafacial Deluxe / Platinum tier, in
    // Skin Tightening 360, Crown Royale and the Microneedling finish.
    // Make the search route clients to the right landing page.
    { name: "Red Light / LED Therapy", category: "Treatment", description: "Included in Hydrafacial Deluxe + Platinum, Skin Tightening 360, Microneedling finish", section: "/laser-and-skin", icon: Sparkles, keywords: ["red light", "red light therapy", "led", "led therapy", "led light", "light therapy", "photobiomodulation", "photobio", "celluma", "infrared", "near infrared", "nir", "pbm", "cold laser", "low level light", "lllt"] },
    { name: "Crown Deluxe Hydrafacial", category: "Facial · includes LED", price: "$199", description: "Hydrafacial + LED Therapy + included booster — the easiest way to add red-light to your routine", section: "/hydrafacial", icon: Sparkles, keywords: ["red light", "led", "hydrafacial", "deluxe", "led therapy", "glow"] },
    { name: "Skin Tightening 360 (includes LED)", category: "Facial · includes LED", description: "Diamond Tip or Dermaplaning + Laser Genesis + Hydrating Mask + LED Therapy", section: "/laser-and-skin", icon: Sparkles, keywords: ["red light", "led", "led therapy", "skin tightening", "360", "tighten", "lift"] },

    // ─── CO₂LIFT CARBOXYTHERAPY (Feb 2026 launch) ────────────────
    { name: "CO₂Lift Carboxytherapy — Education Page", category: "Treatment Page", description: "The no-needle lift · topical CO₂ · Bohr Effect oxygen boost + collagen stimulation", section: "/co2lift-ottawa", icon: Sparkles, keywords: ["co2 lift", "co2lift", "co 2 lift", "carboxy", "carboxytherapy", "carboxy therapy", "no needle lift", "topical co2", "bohr effect", "oxygen facial", "oxygen", "oxygenate", "glow", "needle free", "no needles", "lift"] },
    { name: "CO₂Lift Skin Mousse — Carboxy Moisturizer", category: "Skincare · CO₂Lift", price: "$375", description: "Multi-active daily moisturizer: retinol + Vitamin C + AHA/BHA + hyaluronic acid + CO₂ tech in ONE step", section: "/shop/CO2LIFT-MOUSSE-60G", icon: Sparkles, keywords: ["co2 lift", "co2lift", "mousse", "skin mousse", "carboxy moisturizer", "moisturizer", "retinol", "vitamin c", "hyaluronic", "aha", "bha", "five in one", "5 in 1", "anti aging", "glow"] },
    { name: "CO₂Lift® GLO — Double Set Pack", category: "Skincare · CO₂Lift", price: "$275", description: "2 signature carboxy mask treatments · zero downtime · 45 minutes to glow", section: "/shop/CO2LIFT-GLO-DOUBLE", icon: Sparkles, keywords: ["co2 lift", "glo", "double", "carboxy mask", "face mask", "pre event", "glow mask", "weekend reset"] },
    { name: "CO₂Lift® GLO — Single Pack", category: "Skincare · CO₂Lift", price: "$145", description: "1 signature carboxy mask treatment · the perfect pre-event skin reset", section: "/shop/CO2LIFT-GLO-SINGLE", icon: Sparkles, keywords: ["co2 lift", "glo", "single", "carboxy mask", "single pack", "pre event glow"] },
    { name: "CO₂LIFT® V — 5-Count Intimate Kit", category: "Intimate Wellness · CO₂Lift", price: "$349", description: "Doctor-formulated intimate carboxy gel · 15-day full course · hormone-free · zero downtime", section: "/shop/CO2LIFT-V-5PACK", icon: Sparkles, keywords: ["co2 lift v", "co2lift v", "v kit", "intimate", "vaginal", "feminine", "rejuvenation", "post partum", "menopause", "dryness", "laxity"] },
    { name: "CO₂LIFT® V — 3-Count Intimate Starter", category: "Intimate Wellness · CO₂Lift", price: "$452", description: "3-treatment intro to CO₂LIFT® V · hormone-free · zero downtime", section: "/shop/CO2LIFT-V-3PACK", icon: Sparkles, keywords: ["co2 lift v", "co2lift v", "v starter", "3 pack", "intimate", "vaginal starter"] },

    // ─── LATISSE® PRESCRIPTION LASH SERUM (Feb 2026 launch) ─────
    { name: "Latisse® Eyelash Serum — Education Page", category: "Treatment Page", description: "Prescription lash growth (bimatoprost 0.03%) · longer, thicker, darker lashes in 12-16 weeks", section: "/latisse-ottawa", icon: Sparkles, keywords: ["latisse", "lash serum", "lash growth", "eyelash", "eyelash serum", "longer lashes", "thicker lashes", "bimatoprost", "lash prescription", "lash rx", "rx lash", "lash growth serum", "lash enhancement"] },
    { name: "Latisse® 3 mL — Starter Size", category: "Rx Skincare · Allergan", price: "$149", description: "Starter 6-8 week supply · perfect first bottle · Rx consult included", section: "/shop/LATISSE-3ML", icon: Sparkles, keywords: ["latisse", "3ml", "lash", "starter", "eyelash", "longer lashes", "allergan"] },
    { name: "Latisse® 5 mL — Best Value", category: "Rx Skincare · Allergan", price: "$240", description: "10-14 week supply · the size most clients re-order · gets you through the full 12-16 week course", section: "/shop/LATISSE-5ML", icon: Sparkles, keywords: ["latisse", "5ml", "lash", "best value", "eyelash", "longer lashes", "allergan", "most popular"] },

    // ─── FALL BEAUTY PACKAGES (Oct 2026 launch) ──────────────────
    { name: "HydraFacial + Facial Steam + Lymphatic Drain — NEW Promo", category: "Promo · Tanya · Ottawa", price: "$149", description: "HydraFacial + warm facial steam + lymphatic drainage · 60 min · reg $295 · optional Dermaplaning +$49", section: "/promo/lymphatic-hydrafacial", icon: Sparkles, keywords: ["lymphatic", "lymphatic drainage", "hydrafacial", "hydra facial", "fall promo", "fall", "149", "steam", "facial steam", "tanya", "295", "$149", "facebook", "facebook ad", "fb ad", "promo", "special", "deal", "dermaplaning", "puffy", "puffiness", "glow", "sculpt", "contour", "member promo", "cheryl"] },
    { name: "The Glow Choice — Fall Package", category: "Fall Package · Hero Combo", price: "$1,199", description: "Full Upper Face Botox + 3ml Redensity 1 Skin Booster · save $99", section: "/combos#combos-botox-booster", icon: Sparkles, keywords: ["glow choice", "fall", "fall package", "upper face botox", "redensity", "save 99", "hero combo"] },
    { name: "Nefertiti Lift — Fall", category: "Fall Package · Botox", price: "$479", description: "Jawline + Neck Bands · UNLIMITED units · #1 requested treatment", section: "/combos#combos-botox", icon: Sparkles, keywords: ["nefertiti", "jawline", "neck", "lift", "sagging", "sculpt", "fall"] },
    { name: "Makeover 5 — Fall", category: "Fall Package · Botox", price: "$349", description: "Frown lines + Forehead · UNLIMITED units · entry-point Botox makeover", section: "/book", icon: Sparkles, keywords: ["makeover 5", "frown", "forehead", "11s", "botox makeover", "fall"] },
    { name: "Makeover 9 — Redensity Face + Neck", category: "Fall Package · Skin Booster", price: "$899", description: "Full-face + neck Redensity 1 skin booster · lasting glow + skin-quality", section: "/book", icon: Sparkles, keywords: ["makeover 9", "redensity", "face and neck", "skin booster", "glow", "fall"] },
    { name: "Regenerie Package — Fall Flagship", category: "Fall Package · Filler + Booster", price: "$1,399", description: "2mL RHA 1 filler + 3mL Redensity 1 booster · structure + glow in one visit", section: "/combos", icon: Sparkles, keywords: ["regenerie", "regeneri", "rha 1", "redensity", "flagship", "filler booster", "fall"] },
    { name: "Baby Glow — Eight-Point ($699)", category: "Fall Package · Skin Booster", price: "$699", description: "Signature 8-point Redensity 1 refresh · entry-point Baby Glow at Fall price", section: "/book", icon: Sparkles, keywords: ["baby glow", "8 point", "eight point", "redensity", "glow", "fall", "699"] },
  ];

  // Build full search data = catalog services + shop products + static items.
  // Shop products are deduped against staticItems by section path so the
  // hand-written launch entries (CO₂Lift Mousse, Latisse 3mL/5mL, etc.) keep
  // their rich concern-mapping copy and the rest auto-flow in.
  const staticProductRoutes = new Set(
    staticItems
      .map((it) => (it.section || "").toString().toLowerCase())
      .filter((s) => s.startsWith("/shop/"))
  );

  const autoShopItems = (storeProducts || [])
    .filter((p) => p && p.sku)
    .filter((p) => !staticProductRoutes.has(`/shop/${(p.sku || "").toLowerCase()}`))
    .map((p) => {
      // Flatten everything searchable from the product doc into keywords.
      // `key_ingredients` can be either a joined string or an array —
      // normalize to array of tokens before pushing.
      const ingArr = Array.isArray(p.key_ingredients)
        ? p.key_ingredients
        : String(p.key_ingredients || "").split(/[·•,;·]+/);
      const kws = [
        (p.brand || ""),
        (p.category || ""),
        ...((p.skin_types || []) || []),
        ...((p.skin_concerns || []) || []),
        ...((p.product_types || []) || []),
        ...ingArr.map((k) => String(k || "").trim()).filter(Boolean),
      ]
        .map((s) => String(s).toLowerCase())
        .filter(Boolean);
      return {
        name: p.name || p.sku,
        category: `${p.brand || "Skincare"} · ${p.category || "Shop"}`,
        price: typeof p.price_cad === "number" ? `$${p.price_cad.toFixed(0)}` : "",
        description:
          p.short_description ||
          (p.description || "").slice(0, 140) ||
          "",
        duration: "",
        section: `/shop/${p.sku}`,
        icon: Sparkles,
        shopProduct: true,
        ottawa_only: false,
        keywords: kws,
      };
    });

  const searchData = [
    ...catalogServices.map(svc => ({
      name: svc.code ? `#${svc.code} · ${svc.name}` : svc.name,
      category: svc.category || "Service",
      price: svc.price || "",
      description: svc.description || "",
      duration: svc.duration || (svc.duration_minutes ? `${svc.duration_minutes} min` : ""),
      section: svc.variation_id
        ? `/book?service=${encodeURIComponent(svc.name || "")}&variation=${encodeURIComponent(svc.variation_id)}&location=${(svc.locations || [])[0] || svc._loc || "3"}&src=search`
        : (svc.code ? `/t/${svc.code}` : `/book/menu?q=${encodeURIComponent(svc.name || "")}`),
      icon: Sparkles,
      ottawa_only: !!svc.ottawa_only,
      code: svc.code || "",
      keywords: [
        ...(svc.description || "").toLowerCase().split(/\s+/),
        ...(svc.code ? [String(svc.code).toLowerCase(), `#${String(svc.code).toLowerCase()}`, `number ${String(svc.code).toLowerCase()}`] : []),
        ...((svc.locations || []).includes("3") ? ["ottawa"] : []),
        ...((svc.locations || []).includes("1") ? ["carleton place", "carleton"] : []),
        ...((svc.locations || []).includes("2") ? ["kanata"] : []),
      ]
    })),
    ...autoShopItems,
    ...staticItems
  ];

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (query.trim() === "") {
      setResults([]);
      setSymptomResults([]);
      return;
    }

    const q = query.toLowerCase().trim();
    // Iter 447 · Sep 21 2026 · accent-insensitive matching so "eminence"
    // finds "Éminence", "creme" finds "crème", etc. Also feed a
    // "potenza" alias — Square markets this as "RF Microneedling" but
    // clients still type the trademark.
    const stripAccents = (s) => (s || "")
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .replace(/potenza/gi, "rf microneedling")
      .toLowerCase();
    const qFold = stripAccents(q);
    const searchTerms = q.split(' ').filter(t => t.length > 0);
    const searchTermsFold = searchTerms.map(stripAccents);

    // Match symptoms — check if any of the symptom's terms are contained in the query
    // (or vice versa) so partial phrasings like "dark" or "under eye" still hit.
    // Only tokens of length ≥ 3 are allowed to anchor a symptom match so
    // noise words like "c" or "e" in a product search ("c e ferulic") don't
    // accidentally light up every concern that happens to contain the letter.
    const matchedSymptoms = SYMPTOM_CATALOG.filter((sym) => {
      const allTerms = [...sym.terms, sym.en.toLowerCase(), sym.fr.toLowerCase()];
      return allTerms.some((term) => {
        const t = term.toLowerCase();
        if (q.length >= 3 && (t.includes(q) || q.includes(t))) return true;
        return searchTerms.some((w) => w.length >= 3 && t.includes(w));
      });
    });
    setSymptomResults(matchedSymptoms.slice(0, 3));

    const codeQ = q.replace(/^#|^number\s+/i, "").trim().toUpperCase();
    const exactCode = /^[AL]?\d{1,3}$/i.test(codeQ) ? searchData.filter((it) => (it.code || "").toUpperCase() === codeQ) : [];
    const filtered = exactCode.length ? exactCode : searchData.filter(item => {
      // Pre-fold each searchable field once per item so we don't
      // normalize repeatedly in the inner loop.
      const nameF = stripAccents(item.name || "");
      const catF = stripAccents(item.category || "");
      const descF = stripAccents(item.description || "");
      const priceF = stripAccents(item.price || "");
      const durF = stripAccents(item.duration || "");
      const kwsF = (item.keywords || []).map(stripAccents);
      return searchTermsFold.every(term => {
        return (
          nameF.includes(term) ||
          catF.includes(term) ||
          descF.includes(term) ||
          priceF.includes(term) ||
          durF.includes(term) ||
          kwsF.some(k => k.includes(term))
        );
      });
    });

    // Sort: exact name matches first, then services before pages
    const searchQuery = query.toLowerCase().trim();
    const sorted = filtered.sort((a, b) => {
      const aExact = a.name.toLowerCase().includes(searchQuery);
      const bExact = b.name.toLowerCase().includes(searchQuery);
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      const aService = a.section === "services" ? 0 : 1;
      const bService = b.section === "services" ? 0 : 1;
      return aService - bService;
    });

    setResults(sorted.slice(0, 12));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, catalogServices, storeProducts]);

  const handleResultClick = (section) => {
    if (section.startsWith('/')) {
      window.location.href = section;
    } else {
      scrollToSection(section);
    }
    onClose();
    setQuery("");
  };

  // Book-directly shortcut: navigate to the treatment page and let
  // the booking modal auto-open via ?book=1 query param.
  const handleBookDirectly = (e, section) => {
    e.stopPropagation();
    const url = section.startsWith('/')
      ? `${section}${section.includes('?') ? '&' : '?'}book=1`
      : `/?book=1#${section}`;
    window.location.href = url;
    onClose();
    setQuery("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center pt-32"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      <div
        className="relative w-full max-w-2xl mx-4 bg-white rounded-lg shadow-2xl overflow-hidden animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center border-b border-gray-200 px-4">
          <Search className="w-5 h-5 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            placeholder={t("search.placeholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 px-4 py-4 text-lg outline-none"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="search-input"
          />
          <button
            onClick={onClose}
            className="p-3 min-w-[44px] min-h-[44px] text-gray-400 hover:text-gray-600 transition-colors flex items-center justify-center"
            data-testid="search-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {(symptomResults.length > 0 || results.length > 0) && (
          <div className="max-h-[440px] overflow-y-auto">
            {/* Smart symptom matches */}
            {symptomResults.length > 0 && (
              <div className="bg-gradient-to-r from-amber-50 via-white to-amber-50 border-b border-amber-100">
                <p className="px-4 pt-3 pb-2 text-[11px] font-bold uppercase tracking-[2px] text-amber-700 flex items-center gap-1.5">
                  <Zap className="w-3.5 h-3.5" />
                  {language === "fr" ? "Recommandé pour votre préoccupation" : "Recommended for your concern"}
                </p>
                {symptomResults.map((sym, si) => (
                  <div key={si} className="px-4 pb-3">
                    <p className="text-xs font-semibold text-zinc-700 mb-1.5">
                      &ldquo;{language === "fr" ? sym.fr : sym.en}&rdquo;
                    </p>
                    <div className="space-y-1.5">
                      {sym.treatments.map((tx, ti) => (
                        <div
                          key={ti}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-amber-200 hover:border-amber-400 hover:shadow-sm transition-all group"
                          data-testid={`symptom-result-${si}-${ti}`}
                        >
                          <button
                            onClick={() => handleResultClick(tx.route)}
                            className="flex-1 text-left min-w-0"
                          >
                            <p className="text-sm font-semibold text-zinc-900 group-hover:text-amber-700 transition-colors">
                              {tx.label}
                            </p>
                            <p className="text-[11px] text-zinc-500 mt-0.5">{tx.why}</p>
                          </button>
                          <button
                            onClick={(e) => handleBookDirectly(e, tx.route)}
                            className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-[11px] font-semibold shadow-sm hover:shadow transition-all"
                            data-testid={`symptom-book-${si}-${ti}`}
                          >
                            {language === "fr" ? "Réserver" : "Book"}
                            <ChevronRight className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Regular results */}
            {results.map((result, index) => (
              <div
                key={index}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                data-testid={`search-result-${index}`}
              >
                <button
                  onClick={() => handleResultClick(result.section)}
                  className="flex-1 flex items-center gap-4 text-left min-w-0"
                >
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <result.icon className="w-5 h-5 text-gray-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-gray-900 font-medium truncate" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {result.name}
                      </p>
                      {result.ottawa_only && (
                        <span
                          className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-sky-100 text-sky-700 border border-sky-300"
                          data-testid={`search-ottawa-only-chip-${index}`}
                          title={language === "fr" ? "Offert à Ottawa uniquement" : "Ottawa clinic only"}
                        >
                          <MapPin className="w-2.5 h-2.5" /> {language === "fr" ? "Ottawa" : "Ottawa Only"}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 truncate">
                      {result.category}
                      {result.price && <span className="ml-2 text-amber-700">{result.price}</span>}
                      {result.duration && <span className="ml-2 text-gray-400">{result.duration}</span>}
                      {result.description && !result.price && <span className="ml-2">{result.description}</span>}
                    </p>
                  </div>
                </button>
                {/* Only surface the direct-book shortcut when the section is a treatment route */}
                {result.section && result.section.startsWith('/') && !['/before-after','/refer','/cart','/client-portal','/lounge','/lounge/encyclopedia','/virtual-consultation-guide'].includes(result.section.split('#')[0].split('?')[0]) && (
                  <button
                    onClick={(e) => {
                      // Shop products: skip the booking modal and go
                      // straight to the product detail page.
                      if (result.shopProduct || result.section.startsWith('/shop/')) {
                        e.stopPropagation();
                        window.location.href = result.section;
                        onClose();
                        setQuery("");
                      } else {
                        handleBookDirectly(e, result.section);
                      }
                    }}
                    className={`flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-white text-[11px] font-semibold shadow-sm hover:shadow transition-all ${
                      (result.shopProduct || result.section.startsWith('/shop/'))
                        ? 'bg-slate-900 hover:bg-slate-800'
                        : 'bg-amber-500 hover:bg-amber-600'
                    }`}
                    data-testid={`search-book-${index}`}
                    title={
                      (result.shopProduct || result.section.startsWith('/shop/'))
                        ? (language === "fr" ? "Acheter" : "Shop")
                        : (language === "fr" ? "Réserver directement" : "Book directly")
                    }
                  >
                    {(result.shopProduct || result.section.startsWith('/shop/'))
                      ? (language === "fr" ? "Acheter" : "Shop")
                      : (language === "fr" ? "Réserver" : "Book")}
                    <ChevronRight className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {query && results.length === 0 && symptomResults.length === 0 && (
          <div className="px-4 py-8 text-center">
            <p className="text-gray-500" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("search.noResults")} &ldquo;{query}&rdquo;
            </p>
            <p className="text-sm text-gray-400 mt-2">
              {t("search.trySearchingFor")}
            </p>
          </div>
        )}

        {!query && (
          <div className="px-4 py-4">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-3">{t("search.popular")}</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {["Baby Glow", "Nefertiti", "Renergie Package", "Botox Big 3", "Lip Filler", "Crown Reserve", "Combos", "PRP", "Vampire Facelift", "Ottawa"].map((term) => (
                <button
                  key={term}
                  onClick={() => setQuery(term)}
                  className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-full transition-colors"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                  data-testid={`search-tag-${term.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {term}
                </button>
              ))}
            </div>
            <p className="text-xs text-amber-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Zap className="w-3 h-3" />
              {language === "fr" ? "Ou dites-nous votre préoccupation" : "Or tell us your concern"}
            </p>
            <div className="flex flex-wrap gap-2">
              {SYMPTOM_CATALOG.slice(0, 8).map((sym) => (
                <button
                  key={sym.en}
                  onClick={() => setQuery(language === "fr" ? sym.fr : sym.en)}
                  className="px-3 py-1.5 bg-amber-50 hover:bg-amber-100 border border-amber-200 hover:border-amber-400 text-amber-800 text-sm rounded-full transition-colors"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                  data-testid={`symptom-tag-${sym.en.toLowerCase().replace(/\W+/g, '-')}`}
                >
                  {language === "fr" ? sym.fr : sym.en}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
          <p className="text-xs text-gray-400 text-center">
            {t("misc.pressEsc")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
